import { useEffect, useRef, useState } from 'react';
import type { Device } from './api/types';
import { MovementWatcher, type VehicleAlert } from './movementWatcher';

export type { VehicleAlert } from './movementWatcher';

/**
 * Tell the customer when one of their vehicles starts moving or stops.
 * The rules live in MovementWatcher; this only wires them to the live feed.
 */
export function useVehicleAlerts(devices: Device[], enabled: boolean) {
  const watcher = useRef(new MovementWatcher());
  const [alerts, setAlerts] = useState<VehicleAlert[]>([]);

  useEffect(() => {
    const fresh = watcher.current.push(
      devices.map((device) => ({
        id: device.id,
        name: device.name,
        online: device.online,
        speedKph: device.position?.speedKph,
      })),
      Date.now(),
    );

    // The watcher still runs while alerts are off, so turning them back on does
    // not immediately fire everything that changed in the meantime.
    if (fresh.length > 0 && enabled) {
      setAlerts((current) => [...current, ...fresh].slice(-20));
    }
  }, [devices, enabled]);

  const clear = () => setAlerts([]);

  return { alerts, clear };
}

export type NotifyPermission = 'unsupported' | 'default' | 'granted' | 'denied';

/** Desktop notifications, so an alert lands even when the tab is in the background. */
export function useDesktopNotifications() {
  const [permission, setPermission] = useState<NotifyPermission>(() =>
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  );

  const request = async () => {
    if (typeof Notification === 'undefined') return 'unsupported' as const;
    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  };

  const notify = (title: string, body: string, tag: string) => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    // Only when the tab is hidden: an on-screen toast already covers the rest,
    // and two alerts for one event is worse than one.
    if (document.visibilityState === 'visible') return;
    try {
      new Notification(title, { body, tag });
    } catch {
      // Some browsers refuse construction outside a service worker; the toast stands.
    }
  };

  return { permission, request, notify };
}
